import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { waitForAsyncState } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import { userStore } from '../../../stores/global';
import { PATH } from '../constants';
import {
  type MotionEditorSceneCreateProgress,
  createMotionEditorSceneIdFromMotion,
  getMotionEditorSceneNameForJob,
} from '../store/sceneCreateUtils';

export default function MotionEditorCreateSceneRoute() {
  const { motionID } = useParams<{ motionID: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const createRequestIdRef = useRef(0);
  const [progressText, setProgressText] = useState('Creating scene…');
  const [error, setError] = useState<string | null>(null);
  const existingVariationNumber = useMemo(
    () => parseVariationNumber(searchParams.get('variation')),
    [searchParams],
  );
  const outputCount = useMemo(
    () => parseOutputCount(searchParams.get('outputs')),
    [searchParams],
  );
  const sceneName = useMemo(
    () =>
      motionID
        ? getMotionEditorSceneNameForJob(
            searchParams.get('name') ?? undefined,
            motionID,
          )
        : undefined,
    [motionID, searchParams],
  );

  useEffect(() => {
    if (!motionID) {
      setError('No motion selected.');
      return;
    }

    const requestId = ++createRequestIdRef.current;
    let cancelled = false;

    void (async () => {
      try {
        const apiKeyData = await waitForAsyncState(
          userStore.state,
          (state) => state.apiKey,
        );
        const apiKey = apiKeyData?.apiKey;
        if (!apiKey) {
          throw new Error('Failed to authenticate. Please try again.');
        }

        const sceneID = await createMotionEditorSceneIdFromMotion({
          apiKey,
          motionID,
          existingVariationNumber,
          outputCount,
          sceneName,
          onProgress: (_progress: MotionEditorSceneCreateProgress) => {
            if (!cancelled && requestId === createRequestIdRef.current) {
              setProgressText('Creating scene…');
            }
          },
        });

        if (requestId !== createRequestIdRef.current) {
          return;
        }

        navigate(`${PATH}/${encodeURIComponent(sceneID)}`, { replace: true });
      } catch (createError) {
        if (cancelled || requestId !== createRequestIdRef.current) {
          return;
        }

        setError(
          createError instanceof Error
            ? createError.message
            : 'Failed to create scene.',
        );
      }
    })();

    return () => {
      cancelled = true;
      createRequestIdRef.current += 1;
    };
  }, [existingVariationNumber, motionID, navigate, outputCount, sceneName]);

  return (
    <div className="bg-secondary flex min-h-screen items-center justify-center p-6">
      <div className="border-primary bg-primary flex max-w-sm flex-col gap-2 rounded-3xl border p-6 text-center shadow-sm">
        <Text token="16/medium" as="h1" className="text-primary">
          {error ? 'Unable to create scene' : progressText}
        </Text>
        <Text token="14" as="p" className="text-secondary">
          {error ?? 'Adding the selected motion to a new scene.'}
        </Text>
        {error && (
          <Button
            variant="primary"
            size="sm"
            className="mt-3 self-center"
            onClick={() => navigate(PATH, { replace: true })}
          >
            Choose another motion
          </Button>
        )}
      </div>
    </div>
  );
}

function parseOutputCount(value: string | null) {
  if (!value) {
    return 1;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }

  return Math.floor(parsed);
}

function parseVariationNumber(value: string | null) {
  if (!value) {
    return 0;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }

  return Math.floor(parsed);
}
